// Журнал сверок по чёрному списку: здесь видно, ПОЧЕМУ человека не пустили.
//
// Представление полноширинное, колонки списка у него нет. Причина та же, что у воронки: у
// строки журнала нет «карточки», ради которой существует правая колонка — событие целиком
// помещается в саму строку, и читается журнал сверху вниз. Пустая правая колонка рядом была бы
// поверхностью, которая ничего не означает.
//
// ## Пустой журнал — это не «все чисты»
//
// Сверка идёт сама при приёме отклика и при оформлении, но её могло не быть ни разу: площадка
// не подключена, кандидатов заводят руками. Пустой журнал в этом случае означает «никого ещё не
// сверяли», и подменять этот факт словом «чисто» нельзя — по нему человека возьмут на объект
// (правило 7: пустое, нулевое и неизвестное выглядят по-разному).

import { RiSearchEyeLine } from '@remixicon/react';
import { Link, useNavigate } from 'react-router';

import { countLabel } from '@/shared/text/plural';
import { formatDateDMY, formatTimeHM, parseMoment } from '@/shared/time/format';
import * as Badge from '@/shared/ui/badge';
import { Block } from '@/shared/ui/block';
import * as Button from '@/shared/ui/button';
import * as Hint from '@/shared/ui/hint';
import * as LinkButton from '@/shared/ui/link-button';
import { EmptyState, GridSkeleton, ScreenState } from '@/shared/ui/screen-state';
import { Toolbar, ToolbarField } from '@/shared/ui/toolbar';

import type { BlacklistCheckLogOut } from '../api/blacklist';
import type { CheckResult } from '../api/candidates';
import { CHECK_FORMS, CHECK_RESULT_COLORS } from '../dictionaries';
import { useChecksQuery } from '../queries';
import type { HiringRoute } from '../route';
import { FILTER_CANDIDATE, hiringHref, setFilter } from '../route';

export function ChecksView({
  route,
}: {
  route: HiringRoute;
}) {
  const navigate = useNavigate();
  const { search } = route;

  const кандидат = search.filters[FILTER_CANDIDATE];
  const query = useChecksQuery(кандидат);

  return (
    <div className='flex h-full min-w-0 flex-1 flex-col'>
      <Toolbar
        actions={
          кандидат === undefined ? undefined : (
            <Button.Root
              type='button'
              variant='neutral'
              mode='stroke'
              size='xsmall'
              onClick={() =>
                navigate(hiringHref('checks', setFilter(search, FILTER_CANDIDATE, undefined)), {
                  replace: true,
                })
              }
            >
              Показать все сверки
            </Button.Root>
          )
        }
      >
        {/* Наложенный отбор виден словами, а не только пустотой в списке. Пока он молчал,
            короткий журнал одного кандидата читался как «сверок почти не было» по всей
            системе. */}
        {кандидат !== undefined && (
          <ToolbarField label='Отбор'>Сверки одного кандидата</ToolbarField>
        )}
      </Toolbar>

      <div className='min-h-0 flex-1 overflow-y-auto p-5'>
        <Block
          title={
            query.data === undefined ? 'Журнал сверок' : countLabel(query.data.total, CHECK_FORMS)
          }
          note='Сверка идёт сама при приёме отклика и при оформлении. Здесь видно, почему человека не пустили.'
          flush
        >
          <ScreenState
            data={query.data}
            error={query.error}
            subject='журнал сверок по чёрному списку'
            isEmpty={(data) => data.items.length === 0}
            onRetry={() => void query.refetch()}
            loading={
              <div className='p-5'>
                <GridSkeleton rows={8} band={false} />
              </div>
            }
            empty={
              <div className='p-5'>
                <EmptyState
                  icon={RiSearchEyeLine}
                  title={
                    кандидат === undefined
                      ? 'Сверок ещё не было'
                      : 'По этому кандидату сверок ещё не было'
                  }
                  description={
                    кандидат === undefined
                      ? 'Ни одного человека по чёрному списку ещё не сверяли. Это не значит, что все чисты: сверка запускается сама при приёме отклика с площадки и при оформлении, а до тех пор о людях просто ничего не известно.'
                      : 'Этого человека по чёрному списку ещё не сверяли. Сверка пройдёт сама, когда его поведут на оформление; запустить её раньше можно из его карточки.'
                  }
                  action={
                    кандидат === undefined ? undefined : (
                      <LinkButton.Root variant='primary' size='medium' underline asChild>
                        <Link
                          to={hiringHref('checks', setFilter(search, FILTER_CANDIDATE, undefined))}
                        >
                          Показать все сверки
                        </Link>
                      </LinkButton.Root>
                    )
                  }
                />
              </div>
            }
          >
            {(data) => (
              <>
                <ul className='divide-y divide-stroke-soft-200'>
                  {data.items.map((строка) => (
                    <li key={строка.id}>
                      <СтрокаЖурнала сверка={строка} />
                    </li>
                  ))}
                </ul>
                {data.items.length < data.total && (
                  <div className='px-5 py-3'>
                    <Hint.Root>
                      Показаны последние {data.items.length} сверок из {data.total}. Чтобы увидеть
                      более ранние по конкретному человеку, откройте журнал из его карточки.
                    </Hint.Root>
                  </div>
                )}
              </>
            )}
          </ScreenState>
        </Block>
      </div>
    </div>
  );
}

function СтрокаЖурнала({ сверка }: { сверка: BlacklistCheckLogOut }) {
  const когда = parseMoment(сверка.created_at);
  const признак = (сверка.signal_title ?? '').trim();

  return (
    <div className='flex items-start justify-between gap-4 px-5 py-3'>
      <div className='flex min-w-0 flex-col gap-0.5'>
        <span className='tabular-nums text-label-sm text-text-strong-950'>
          {когда === null
            ? 'дата сверки не записана'
            : `${formatDateDMY(когда)} в ${formatTimeHM(когда)}`}
        </span>
        <span className='flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-paragraph-xs text-text-sub-600'>
          {/* «Чисто» — это ответ, а не отсутствие ответа: признаку в такой строке взяться
              неоткуда, и писать «признак не записан» значило бы намекать на потерю данных
              там, где ничего не потеряно (правило 7). */}
          <span>
            {сверка.stage_title} ·{' '}
            {признак !== ''
              ? `сработало по признаку «${признак}»`
              : сверка.result === 'clear'
                ? 'совпадений не нашлось'
                : 'признак совпадения не записан'}
          </span>
          {сверка.candidate_id !== null && сверка.candidate_id !== undefined && (
            <LinkButton.Root variant='primary' size='small' underline asChild>
              <Link to={hiringHref('candidates', { filters: {}, panels: [] }, сверка.candidate_id)}>
                Кандидат
              </Link>
            </LinkButton.Root>
          )}
          {сверка.entry_id !== null && сверка.entry_id !== undefined && (
            <LinkButton.Root variant='primary' size='small' underline asChild>
              <Link to={hiringHref('blacklist', { filters: {}, panels: [] }, сверка.entry_id)}>
                Запись чёрного списка
              </Link>
            </LinkButton.Root>
          )}
        </span>
      </div>
      <Badge.Root
        variant='lighter'
        color={CHECK_RESULT_COLORS[сверка.result as CheckResult] ?? 'gray'}
        size='medium'
      >
        {сверка.result_title}
      </Badge.Root>
    </div>
  );
}
